import React from 'react';
import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
} from '@mui/material';

import type { PropertyRepresentativesSectionProps } from '../../types/property.types';

import { DISABLED_DEPARTMENT_CODE } from '@/lib/constants';
import { Staff } from '@/features/companies/types';
import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles/common-style';

// Helper function to get staff helper text
const getStaffHelperText = (
  fieldError: string,
  isLoaded: boolean,
  hasStaffs: boolean,
  departmentName: string,
  fieldValue: string | number | undefined,
  staffType: string
): string => {
  if (fieldError) return fieldError;
  if (isLoaded && !hasStaffs) return `部署「${departmentName}」に${staffType}がいません`;
  if (!fieldValue) return `${staffType}は必修項目です。`;
  return '';
};

export const PropertyRepresentativesSection: React.FC<PropertyRepresentativesSectionProps> = ({
  formData,
  originalFormData,
  onUpdateField,
  getFieldError,
  isFieldValid,
  departments,
  departmentsLoading,
  departmentSelected,
  salesStaffs,
  salesStaffSelected,
  deliveryStaffs,
  deliveryStaffSelected,
  staffsLoading,
  onDepartmentChange,
}) => {
  // Note: Staff loading is handled by the parent component (use-property-registration-form.ts)

  const handleDepartmentChange = (departmentCode: string) => {
    const departmentCodeNum = Number.parseInt(departmentCode);

    onUpdateField('department_code', departmentCodeNum);
    // Clear sales and delivery staff when department changes
    onUpdateField('sales_representative_code', '');
    onUpdateField('delivery_person_code', '');

    // Find department by code (handle both string and number)
    const department = departments?.find(
      (dept) => dept.code?.toString() === departmentCodeNum.toString()
    );

    if (department) {
      onDepartmentChange(department.id);
    }
  };

  const isSalesStaffDisabled = !formData.department_code;
  const isDeliveryStaffDisabled = !formData.department_code;

  // Get department name for error messages
  const selectedDepartment = departments?.find((dept) => dept.code === formData.department_code);
  const departmentName = selectedDepartment?.name || '';

  // Check if staffs are loaded but empty
  const hasSalesStaffs = salesStaffs && salesStaffs.length > 0;
  const hasDeliveryStaffs = deliveryStaffs && deliveryStaffs.length > 0;
  const isStaffsLoaded = !staffsLoading && Boolean(formData.department_code);

  const renderPersonSelect = (
    selectedCode: string,
    staffs: Staff[],
    staffSelected: Staff | null
  ) => {
    if (staffs && staffs.length > 0) {
      const currentStaff = staffs.find((staff) => staff.code === Number(selectedCode));
      if (currentStaff) {
        return `${currentStaff.lastName || ''} ${currentStaff.firstName || ''}`;
      }
    }
    return `${staffSelected?.lastName || ''} ${staffSelected?.firstName || ''}`;
  };

  const renderDepartmentSelect = (selectedCode: string) => {
    // If no selectedCode, empty, or "0", return empty string (for empty state)
    if (!selectedCode || selectedCode === '' || selectedCode === '0') {
      return '';
    }
    // Try to find department in departments list first
    if (departments && departments.length > 0) {
      const currentDepartment = departments.find(
        (department) => department.code?.toString() === selectedCode.toString()
      );
      if (currentDepartment) {
        return currentDepartment.name;
      }
    }
    // Fallback to departmentSelected if available
    if (departmentSelected) {
      return departmentSelected.name || '';
    }
    // Last resort: return the code itself to ensure we have a value
    return selectedCode;
  };

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }}>
      <Typography
        variant='h5'
        fontWeight='bold'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
        }}
      >
        野原担当
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 担当部署 */}
        <FormControl
          fullWidth
          required
          error={!isFieldValid('department_code')}
          disabled={departmentsLoading}
        >
          <InputLabel>担当部署</InputLabel>
          <Select
            value={
              formData.department_code && formData.department_code !== 0
                ? formData.department_code.toString()
                : ''
            }
            onChange={(e) => handleDepartmentChange(e.target.value)}
            label='担当部署'
            renderValue={(selectedCode) => {
              if (!selectedCode || selectedCode === '' || selectedCode === '0') {
                return '';
              }
              const displayValue = renderDepartmentSelect(selectedCode);
              // Ensure we always return a valid non-empty string when there's a selectedCode
              return displayValue || selectedCode;
            }}
            slotProps={{
              input: {
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.department_code,
                  formData.department_code
                ),
              },
            }}
          >
            <MenuItem value=''>担当部署を選択</MenuItem>
            {departments?.map((dept) => (
              <MenuItem
                key={dept.id}
                value={dept.code?.toString() || ''}
                disabled={dept.code === DISABLED_DEPARTMENT_CODE}
              >
                {dept.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{getFieldError('department_code')}</FormHelperText>
        </FormControl>

        {/* 営業担当者 */}
        <FormControl
          fullWidth
          required
          error={!isFieldValid('sales_representative_code')}
          disabled={isSalesStaffDisabled || staffsLoading}
        >
          <InputLabel>営業担当者</InputLabel>
          <Select
            value={formData.sales_representative_code?.toString() || ''}
            onChange={(e) => onUpdateField('sales_representative_code', e.target.value)}
            label='営業担当者'
            renderValue={(selectedCode) => {
              return renderPersonSelect(selectedCode, salesStaffs, salesStaffSelected ?? null);
            }}
            slotProps={{
              input: {
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.sales_representative_code,
                  formData.sales_representative_code
                ),
              },
            }}
          >
            <MenuItem value=''>営業担当者を選択</MenuItem>
            {!salesStaffs || salesStaffs.length === 0 ? (
              <MenuItem value='' disabled>
                {staffsLoading ? '読み込み中...' : '営業担当者がいません'}
              </MenuItem>
            ) : (
              salesStaffs.map((staff) => (
                <MenuItem key={staff.id} value={staff.code || ''}>
                  {`${staff.lastName} ${staff.firstName}`}
                </MenuItem>
              ))
            )}
          </Select>
          <FormHelperText>
            {getStaffHelperText(
              getFieldError('sales_representative_code'),
              isStaffsLoaded,
              hasSalesStaffs,
              departmentName,
              formData.sales_representative_code,
              '営業担当者'
            )}
          </FormHelperText>
        </FormControl>

        {/* デリバリー担当者 */}
        <FormControl
          fullWidth
          required
          error={!isFieldValid('delivery_person_code')}
          disabled={isDeliveryStaffDisabled || staffsLoading}
        >
          <InputLabel>デリバリー担当者</InputLabel>
          <Select
            value={formData.delivery_person_code?.toString() || ''}
            onChange={(e) => onUpdateField('delivery_person_code', e.target.value)}
            label='デリバリー担当者'
            renderValue={(selectedCode) => {
              return renderPersonSelect(
                selectedCode,
                deliveryStaffs,
                deliveryStaffSelected ?? null
              );
            }}
            slotProps={{
              input: {
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.delivery_person_code,
                  formData.delivery_person_code
                ),
              },
            }}
          >
            <MenuItem value=''>デリバリー担当者を選択</MenuItem>
            {!deliveryStaffs || deliveryStaffs.length === 0 ? (
              <MenuItem value='' disabled>
                {staffsLoading ? '読み込み中...' : 'デリバリー担当者がいません'}
              </MenuItem>
            ) : (
              deliveryStaffs.map((staff) => (
                <MenuItem key={staff.id} value={staff.code || ''}>
                  {`${staff.lastName} ${staff.firstName}`}
                </MenuItem>
              ))
            )}
          </Select>
          <FormHelperText>
            {getStaffHelperText(
              getFieldError('delivery_person_code'),
              isStaffsLoaded,
              hasDeliveryStaffs,
              departmentName,
              formData.delivery_person_code,
              'デリバリー担当者'
            )}
          </FormHelperText>
        </FormControl>
      </Box>
    </PaperBoxStyles>
  );
};
